import { useState } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Lock, 
  Eye, 
  Users, 
  BarChart3,
  Shield,
  Download,
  Trash2,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react'
import { toast } from 'sonner'

export function PrivacySettings() {
  const { privacy, updatePrivacy, exportData, deleteAccount, loading } = useSettingsStore()
  const [hasChanges, setHasChanges] = useState(false)
  const [formData, setFormData] = useState(privacy)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')

  const handleFieldChange = (field: keyof typeof privacy, value: any) => {
    const newData = { ...formData, [field]: value }
    setFormData(newData)
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      await updatePrivacy(formData)
      setHasChanges(false)
      toast.success('Privacy settings updated!')
    } catch (error) {
      toast.error('Failed to update privacy settings')
    }
  }

  const handleReset = () => {
    setFormData(privacy)
    setHasChanges(false)
  }

  const handleExportData = async () => {
    try {
      await exportData()
      toast.success('Data export started - download will begin shortly')
    } catch (error) {
      toast.error('Failed to export data')
    }
  }

  const handleDeleteAccount = async () => {
    if (!deletePassword.trim()) {
      toast.error('Please enter your password')
      return
    }
    
    try {
      await deleteAccount(deletePassword)
      toast.success('Account deletion request submitted')
      setShowDeleteConfirm(false)
      setDeletePassword('')
    } catch (error) {
      toast.error('Failed to delete account')
    }
  }

  const profileVisibilityOptions = [
    { 
      value: 'public', 
      label: 'Public', 
      description: 'Visible to anyone on the internet',
      icon: Eye
    },
    { 
      value: 'team_only', 
      label: 'Team Only', 
      description: 'Only visible to your team members',
      icon: Users
    },
    { 
      value: 'private', 
      label: 'Private', 
      description: 'Only visible to you',
      icon: Lock
    }
  ]

  const privacyOptions = [
    {
      key: 'analytics_tracking' as const,
      label: 'Analytics Tracking',
      description: 'Help us improve by sharing anonymous usage data',
      icon: BarChart3
    },
    {
      key: 'data_sharing' as const,
      label: 'Data Sharing',
      description: 'Share aggregated data with third-party services',
      icon: Shield
    },
    {
      key: 'marketing_communications' as const,
      label: 'Marketing Communications',
      description: 'Receive promotional emails and product updates',
      icon: Info
    },
    {
      key: 'activity_tracking' as const,
      label: 'Activity Tracking',
      description: 'Track your activity for personalized recommendations',
      icon: BarChart3
    }
  ]

  return (
    <div className="space-y-6">
      {/* Profile Visibility */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Eye className="h-5 w-5" />
            <span>Profile Visibility</span>
          </CardTitle>
          <CardDescription>
            Control who can see your profile information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {profileVisibilityOptions.map((option) => {
              const Icon = option.icon
              const isSelected = formData.profile_visibility === option.value
              
              return (
                <div
                  key={option.value}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleFieldChange('profile_visibility', option.value)}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="flex-1">
                      <div className="font-medium">{option.label}</div>
                      <div className="text-sm text-muted-foreground">
                        {option.description}
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="h-5 w-5 text-primary" />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Data & Privacy Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>Data & Privacy</span>
          </CardTitle>
          <CardDescription>
            Manage how your data is used and shared
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {privacyOptions.map((option) => {
              const Icon = option.icon
              return (
                <div key={option.key} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="font-medium">{option.label}</div>
                      <div className="text-sm text-muted-foreground">
                        {option.description}
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={formData[option.key]}
                    onCheckedChange={(checked) => handleFieldChange(option.key, checked)}
                  />
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Privacy Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Privacy Summary</CardTitle>
          <CardDescription>
            Overview of your current privacy settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Profile Visibility</div>
                <div className="text-sm text-muted-foreground">
                  {profileVisibilityOptions.find(o => o.value === formData.profile_visibility)?.label}
                </div>
              </div>
              <Badge variant="outline">
                {formData.profile_visibility}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {Object.values(formData).filter(v => v === true).length}
                </div>
                <div className="text-sm text-muted-foreground">Privacy options enabled</div>
              </div>
              <div className="p-4 border rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {Object.values(formData).filter(v => v === false).length}
                </div>
                <div className="text-sm text-muted-foreground">Privacy options disabled</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Download className="h-5 w-5" />
            <span>Data Management</span>
          </CardTitle>
          <CardDescription>
            Export or delete your personal data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Export Your Data</div>
                <div className="text-sm text-muted-foreground">
                  Download a copy of all your data in JSON format
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleExportData}
                disabled={loading}
                className="flex items-center space-x-2"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span>Export Data</span>
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg border-red-200">
              <div>
                <div className="font-medium text-red-600">Delete Account</div>
                <div className="text-sm text-muted-foreground">
                  Permanently delete your account and all associated data
                </div>
              </div>
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete Account</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Account Confirmation */}
      {showDeleteConfirm && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              <span>Delete Account</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This action cannot be undone. This will permanently delete your account and all associated data.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="delete-password" className="text-sm font-medium">
                  Enter your password to confirm
                </label>
                <input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter your password"
                />
              </div>
              
              <div className="flex items-center justify-end space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setDeletePassword('')
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={loading || !deletePassword.trim()}
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  Delete Account
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Changes */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Badge variant="outline" className="text-orange-600">
              Unsaved changes
            </Badge>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || loading}
            className="flex items-center space-x-2"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  )
} 